import _ from "lodash";
import { getUnixTime } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";

import { inRange } from "@sportal/lib";
import { SCHEDULE_TYPES } from "../../pages/profile/scheduler/scheduler.consts";
import { getTimezone } from "../account";
import { RootState } from "../root.reducer";
import { getRemovedItems } from "./schedules.helper";
import { SchedulesState } from "./schedules.types";
import { Schedule, ScheduleStore } from "@sportal/api";
import { getToggles } from "../../pages/profile/scheduler/scheduler.helpers";

export const getSchedulesState = (state: RootState): SchedulesState =>
  state.schedules;

export const getBlockedCategories = (profileId: string) => (state: RootState) =>
  getSchedulesState(state).changed.blockedCategories[profileId] || [];

export const getProfileSchedules =
  (profileId: string) => (state: RootState) => {
    const allSchedules = getSchedulesState(state).changed.list;
    return _.filter(allSchedules, { profile: profileId });
  };

export const getProfileSchedulesByType =
  (profileId, type) =>
  (state: RootState): ScheduleStore[] => {
    const profileSchedules = getProfileSchedules(profileId)(state);

    return _.filter(profileSchedules, { type });
  };

export const getProfileScheduleByName = (state, profileId, scheduleName) => {
  const schedules = getProfileSchedules(profileId)(state);
  return _.find(schedules, ["name", scheduleName]);
};

export const isScheduleEnabled = (profileId, type) => (state: RootState) => {
  const schedules = getProfileSchedulesByType(profileId, type)(state);
  const showToggles = getToggles(state);
  const isControlledByToggle = showToggles.includes(type);

  if (isControlledByToggle) {
    return (
      !_.isEmpty(schedules) &&
      _.every(schedules, (schedule) => schedule.enabled)
    );
  }

  return true;
};

export const isScheduleActive = (state, profileId, scheduleName) => {
  const schedule = getProfileScheduleByName(state, profileId, scheduleName);

  if (!schedule) {
    return false;
  }

  const timezone = getTimezone(state);
  const now = getUnixTime(zonedTimeToUtc(Date.now(), timezone));

  const start =
    schedule &&
    getUnixTime(zonedTimeToUtc(schedule.timeStart, schedule.timeZone));
  const end =
    schedule &&
    getUnixTime(zonedTimeToUtc(schedule.timeEnd, schedule.timeZone));

  return (
    schedule.enabled &&
    inRange(
      now,
      {
        start,
        end,
      },
      "()",
    )
  );
};

export const getNextAvailableRuleName =
  (profileId: string, type) => (state: RootState) => {
    const busyNames = _.map(
      getProfileSchedulesByType(profileId, type)(state),
      "name",
    );

    const getName = (index = 0) => {
      const newName = `${type}${index}`;
      if (_.includes(busyNames, newName)) {
        return getName(index + 1);
      }

      return newName;
    };

    return getName();
  };

const STORED_FIELDS = [
  "name",
  "type",
  "enabled",
  "categories",
  "day",
  "timeEnd",
  "timeStart",
];

export const hasCategoriesChanges =
  (profileId: string) => (state: RootState) => {
    const { saved, changed } = getSchedulesState(state);
    return !_.isEqual(
      saved.blockedCategories[profileId],
      changed.blockedCategories[profileId],
    );
  };

export const getRemovedSchedules = (profileId, type) => (state: RootState) => {
  const { saved, changed } = getSchedulesState(state);
  const filter = (schedule) =>
    schedule.profile === profileId && schedule.type === type;

  const removedSchedules = getRemovedItems({ saved, changed });
  return _.filter(removedSchedules, filter);
};

export const dehydrateSchedule = (schedule): Schedule =>
  _.pick(schedule, STORED_FIELDS) as Schedule;

export const getScheduleStatusForSave = (
  state: RootState,
  scheduleId: string,
): { isNew: boolean; isChanged: boolean } => {
  const { saved, changed } = getSchedulesState(state);
  const isNew = !_.has(saved.list, scheduleId);
  const isChanged = !_.isEqual(
    _.pick(saved.list[scheduleId], STORED_FIELDS),
    _.pick(changed.list[scheduleId], STORED_FIELDS),
  );

  return {
    isNew,
    isChanged,
  };
};

export const areRulesChanged =
  (profileId: string, type: string) => (state: RootState) => {
    const filteredRules = getProfileSchedulesByType(profileId, type)(state);

    const hasAddedRules = _.some(
      filteredRules,
      (rule) => getScheduleStatusForSave(state, rule.id).isNew,
    );
    const hasChangedRules = _.some(
      filteredRules,
      (rule) => getScheduleStatusForSave(state, rule.id).isChanged,
    );
    const hasRemovedRules =
      getRemovedSchedules(profileId, type)(state).length > 0;

    return {
      hasAddedRules,
      hasChangedRules,
      hasRemovedRules,
    };
  };

export const hasScheduleChanges =
  (profileId: string, type: string) => (state: RootState) => {
    const { hasAddedRules, hasChangedRules, hasRemovedRules } = areRulesChanged(
      profileId,
      type,
    )(state);
    const hasRulesChanges = hasAddedRules || hasChangedRules || hasRemovedRules;

    if (type === SCHEDULE_TYPES.homework) {
      const hasCategoryChanges = hasCategoriesChanges(profileId)(state);
      return hasRulesChanges || hasCategoryChanges;
    }

    return hasRulesChanges;
  };

export const areRulesReadyForSave =
  (profileId: string, type: string) => (state: RootState) => {
    const { hasAddedRules, hasChangedRules, hasRemovedRules } = areRulesChanged(
      profileId,
      type,
    )(state);
    const rules = getProfileSchedulesByType(profileId, type)(state);
    const hasChangedCategories = hasCategoriesChanges(profileId)(state);
    const blockedCategories = getBlockedCategories(profileId)(state);

    if (rules.length === 0 && hasRemovedRules) return [true, ""];

    if (type === SCHEDULE_TYPES.homework) {
      const hasAnyChanges =
        hasChangedRules ||
        hasAddedRules ||
        hasRemovedRules ||
        hasChangedCategories;
      const hasCategories = !_.isEmpty(blockedCategories);

      if (rules.length === 0 && hasChangedCategories)
        return [false, "rules_missing"];
      if (!hasAnyChanges) return [false, ""];
      if (hasAnyChanges && hasCategories) return [true, ""];
      return [false, "categories_missing"];
    }

    if (type === SCHEDULE_TYPES.internetOff) {
      if (rules.length === 0 && !hasChangedRules) return [false, ""];

      return [hasChangedRules || hasAddedRules || hasRemovedRules, ""];
    }
  };
